import { useContext } from "react";
import styled from "styled-components";
import WithBackground from "../../core/boilerplate/WithBackground";
import LoggedOutHeader from "../loggedOut/landingPage/LoggedOutHeader";
import Footer from "../loggedOut/landingPage/Footer";
import Context from "../../../Context";

const ScrollWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;
  height: 100%;
  overflow-y: auto;
  gap: 100px;

  @media (max-width: 900px) {
    gap: 30px;
  }
`;

const Content = styled.div`
  width: 62vw;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    width: 90vw;
  }
`;

const Title = styled.div`
  font-weight: 900;
  font-size: 30px;
  margin-bottom: 30px;
  color: var(--accent);
  width: 90%;
  text-transform: uppercase;
  font-family: "Montserrat", sans-serif;
`;

const Text = styled.div`
  line-height: 1.8;
  font-size: 15px;
  color: var(--element);

  h2 {
    font-size: 22px;
    font-weight: 700;
    margin-top: 35px;
    margin-bottom: 10px;
    color: var(--accent);
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin-top: 20px;
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 12px;
  }

  ul {
    padding-left: 20px;
    margin-bottom: 15px;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: var(--accent);
    text-decoration: underline;
  }

  strong {
    font-weight: 600;
  }

  @media (max-width: 900px) {
    font-size: 14px;

    h2 {
      font-size: 19px;
      margin-top: 25px;
    }

    h3 {
      font-size: 16px;
    }
  }
`;

export default function PrivacyPolicy() {
  const { loggedInUserId } = useContext(Context);

  const privacyPolicyContent = `
    <p><strong>Last updated:</strong> March 15, 2026</p>

    <h2>1. Introduction</h2>
    <p>Welcome to Karuna ("we," "our," or "us"). Karuna is a platform that connects companies, NGOs, and volunteers to create collective social impact. We are committed to protecting your personal data and respecting your privacy.</p>
    <p>This Privacy Policy explains what information we collect, how we use it, and your rights regarding your data when you use our website and services.</p>

    <h2>2. Information We Collect</h2>

    <h3>Information you provide to us</h3>
    <ul>
      <li><strong>Account Information:</strong> When you sign up, we collect your name, email address, and profile details. If you sign in with Google, we receive your name, email, and profile picture from your Google account.</li>
      <li><strong>Profile Information:</strong> Depending on your role (company, NGO, or volunteer), you may provide organization details, areas of interest, skills, availability, and CSR-related information.</li>
      <li><strong>Communications:</strong> Messages, feedback, and other content you share through the platform.</li>
    </ul>

    <h3>Information collected automatically</h3>
    <ul>
      <li><strong>Device &amp; Browser Data:</strong> IP address, browser type and version, operating system, and device identifiers.</li>
      <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the platform, and interaction patterns.</li>
      <li><strong>Location Data:</strong> Approximate location based on your IP address, or precise location if you grant permission (used to match you with nearby opportunities).</li>
    </ul>

    <h2>3. How We Use Your Information</h2>
    <p>We use your information to:</p>
    <ul>
      <li>Create and manage your account.</li>
      <li>Connect companies, NGOs, and volunteers based on shared interests and goals.</li>
      <li>Facilitate communication between users on the platform.</li>
      <li>Show you relevant social impact opportunities and projects.</li>
      <li>Send important service updates and notifications.</li>
      <li>Improve and personalize your experience on Karuna.</li>
      <li>Ensure platform security and prevent fraud.</li>
      <li>Comply with legal obligations.</li>
    </ul>

    <h2>4. How We Share Your Information</h2>
    <p>We do not sell your personal data. We may share your information in the following cases:</p>
    <ul>
      <li><strong>With other users:</strong> Your profile information (name, organization, interests) is visible to other users to facilitate connections. You control what details are public on your profile.</li>
      <li><strong>Service providers:</strong> We work with third-party services (hosting, analytics, authentication) that process data on our behalf under strict agreements.</li>
      <li><strong>Legal requirements:</strong> We may disclose your data if required by law, regulation, or legal process.</li>
      <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction.</li>
    </ul>

    <h2>5. Third-Party Services</h2>
    <p>Our platform integrates with the following third-party services:</p>
    <ul>
      <li><strong>Google Authentication:</strong> For account sign-in. Subject to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</li>
      <li><strong>Firebase:</strong> For authentication and backend services. Subject to <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Firebase's Privacy Policy</a>.</li>
      <li><strong>Google Maps:</strong> For location-based features. Subject to <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a>.</li>
    </ul>

    <h2>6. Data Security</h2>
    <p>We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>

    <h2>7. Data Retention</h2>
    <p>We retain your personal data for as long as your account is active or as needed to provide you our services. If you delete your account, we will remove your personal data within 30 days, except where we are required to retain it for legal or regulatory purposes.</p>

    <h2>8. Your Rights</h2>
    <p>You have the right to:</p>
    <ul>
      <li><strong>Access</strong> the personal data we hold about you.</li>
      <li><strong>Correct</strong> inaccurate or incomplete data.</li>
      <li><strong>Delete</strong> your account and personal data.</li>
      <li><strong>Object</strong> to or restrict certain processing of your data.</li>
      <li><strong>Data portability</strong> — receive a copy of your data in a structured format.</li>
      <li><strong>Withdraw consent</strong> at any time where we rely on consent to process your data.</li>
    </ul>
    <p>To exercise any of these rights, please contact us using the details below.</p>

    <h2>9. Cookies</h2>
    <p>We use essential cookies and local storage to keep you signed in and remember your preferences (such as language settings). We do not use tracking or advertising cookies.</p>

    <h2>10. Children's Privacy</h2>
    <p>Karuna is not intended for children under the age of 16. We do not knowingly collect personal data from children. If you believe a child has provided us with their data, please contact us and we will promptly delete it.</p>

    <h2>11. Changes to This Policy</h2>
    <p>We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice on our platform or sending you an email. Your continued use of Karuna after changes are posted constitutes your acceptance of the updated policy.</p>

    <h2>12. Contact Us</h2>
    <p>If you have any questions about this Privacy Policy or how we handle your data, please reach out to us:</p>
    <p>Email: <a href="mailto:privacy@karuna.org">privacy@karuna.org</a></p>
  `;

  return (
    <WithBackground>
      <ScrollWrapper>
        {loggedInUserId ? null : <LoggedOutHeader />}

        <Content>
          <Title>Privacy Policy</Title>
          <Text dangerouslySetInnerHTML={{ __html: privacyPolicyContent }} />
          <br />
          <br />
          <br />
          <br />
        </Content>
        <Footer />
      </ScrollWrapper>
    </WithBackground>
  );
}
